import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { Plus, X, AlertTriangle, ShieldCheck, UploadCloud } from "lucide-react";

const RISKS = ["low", "medium", "high", "restricted", "do_not_sell"];
const LEGAL = ["pending", "approved", "rejected"];
const US = ["allowed", "manual_review", "prohibited"];
const DEA = ["not_screened", "clear", "needs_legal_review", "prohibited"];

const empty = {
  sku: "", product_name: "", cas_number: "", molecular_formula: "", molecular_weight: "",
  sequence: "", appearance: "Lyophilized white powder", storage_conditions: "Store at -20°C, protect from light",
  testing_method: "HPLC, MS", purity_percent: "", category: "Reference Material", description: "",
  risk_level: "medium", legal_review_status: "pending", us_sale_status: "manual_review",
  dea_screen_status: "not_screened", requires_coa: true, allows_public_listing: false, allows_checkout: false,
  restricted_states: [], restricted_countries: [], notes_for_admin: "",
  price_usd: 0, pack_size: "10 mg", image_url: "",
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [findings, setFindings] = useState([]);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pushing, setPushing] = useState(null);       // sku currently being pushed
  const [pushModal, setPushModal] = useState(null);   // {sku} while collecting creds
  const [pushForm, setPushForm] = useState({
    prod_api_url: sessionStorage.getItem("axn_prod_api_url") || "https://nebulaaminos.com",
    prod_email:   sessionStorage.getItem("axn_prod_email")   || "support@nebulaaminos.com",
    prod_password: "",
    override_compliance: true,
  });

  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const open = (p) => {
    setEditing(p?.sku || "new");
    setForm(p ? { ...empty, ...p } : empty);
    setFindings([]); setOverride(false);
  };
  const close = () => { setEditing(null); setForm(empty); setFindings([]); };

  const save = async () => {
    setBusy(true);
    try {
      const headers = override ? { "X-Compliance-Override": "1" } : {};
      const url = editing === "new" ? "/admin/products" : `/admin/products/${editing}`;
      const method = editing === "new" ? "post" : "put";
      const { data } = await api[method](url, form, { headers });
      if (data.blocked) {
        setFindings(data.findings);
        toast.warning(`Blocked claims detected (${data.findings.length}). Resolve or override.`);
      } else {
        toast.success("Saved");
        close(); load();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (sku) => {
    if (!confirm(`Delete ${sku}?`)) return;
    await api.delete(`/admin/products/${sku}`);
    toast.success("Deleted");
    load();
  };

  const openPush = (sku) => setPushModal({ sku });
  const closePush = () => { setPushModal(null); setPushForm((f) => ({ ...f, prod_password: "" })); };

  const runPush = async () => {
    if (!pushModal) return;
    if (!pushForm.prod_password) { toast.error("Enter your production admin password."); return; }
    setPushing(pushModal.sku);
    try {
      const { data } = await api.post(`/admin/products/${pushModal.sku}/push-to-production`, {
        prod_api_url: pushForm.prod_api_url,
        prod_email: pushForm.prod_email,
        prod_password: pushForm.prod_password,
        override_compliance: pushForm.override_compliance,
      });
      // Persist non-secret prefs for next click.
      sessionStorage.setItem("axn_prod_api_url", pushForm.prod_api_url);
      sessionStorage.setItem("axn_prod_email", pushForm.prod_email);
      toast.success(`Pushed ${pushModal.sku} — ${data.action} on production`);
      closePush();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Push failed");
    } finally { setPushing(null); }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Catalog</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Products</h1>
        </div>
        <button onClick={() => open(null)} className="btn-primary inline-flex items-center gap-2" data-testid="new-product">
          <Plus className="h-4 w-4" /> New product
        </button>
      </div>

      <div className="mt-6 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">SKU</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Risk</th>
              <th className="text-left px-4 py-2">Legal</th>
              <th className="text-left px-4 py-2">US Sale</th>
              <th className="text-left px-4 py-2">Public</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.sku} data-testid={`admin-product-row-${p.sku}`}>
                <td className="px-4 py-3 font-mono">{p.sku}</td>
                <td className="px-4 py-3">{p.product_name}</td>
                <td className="px-4 py-3"><ComplianceBadge kind="risk_level" value={p.risk_level} /></td>
                <td className="px-4 py-3"><ComplianceBadge kind="legal_review_status" value={p.legal_review_status} /></td>
                <td className="px-4 py-3"><ComplianceBadge kind="us_sale_status" value={p.us_sale_status} /></td>
                <td className="px-4 py-3 font-mono text-xs">{p.allows_public_listing ? "YES" : "NO"}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => open(p)} className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline" data-testid={`edit-${p.sku}`}>Edit</button>
                  <button
                    onClick={() => openPush(p.sku)}
                    disabled={pushing === p.sku}
                    className="text-xs font-mono uppercase tracking-wider text-[#0B6623] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`push-prod-${p.sku}`}
                    title="Push this product to production"
                  >
                    <UploadCloud className="h-3.5 w-3.5" /> {pushing === p.sku ? "Pushing…" : "Push → Prod"}
                  </button>
                  <button onClick={() => del(p.sku)} className="text-xs font-mono uppercase tracking-wider text-[#A30414] hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={close}>
          <div className="w-full max-w-2xl h-full bg-white border-l border-[#D1D5DB] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[#D1D5DB] flex items-center justify-between sticky top-0 bg-white">
              <div>
                <div className="label-eyebrow">{editing === "new" ? "Create" : "Edit"}</div>
                <div className="font-bold tracking-tight">{editing === "new" ? "New product" : form.sku}</div>
              </div>
              <button onClick={close} className="p-2 hover:bg-[#F7F7F8]" aria-label="Close" data-testid="close-product-modal"><X className="h-4 w-4" /></button>
            </div>

            <div className="px-6 py-6 space-y-4">
              <Row><F label="SKU" v={form.sku} on={(v) => setForm({ ...form, sku: v })} disabled={editing !== "new"} test="f-sku" /></Row>
              <Row><F label="Product name" v={form.product_name} on={(v) => setForm({ ...form, product_name: v })} test="f-name" /></Row>
              <Row>
                <F label="CAS Number" v={form.cas_number} on={(v) => setForm({ ...form, cas_number: v })} test="f-cas" />
                <F label="Molecular formula" v={form.molecular_formula} on={(v) => setForm({ ...form, molecular_formula: v })} test="f-mf" />
              </Row>
              <Row>
                <F label="Molecular weight" v={form.molecular_weight} on={(v) => setForm({ ...form, molecular_weight: v })} test="f-mw" />
                <F label="Sequence" v={form.sequence} on={(v) => setForm({ ...form, sequence: v })} test="f-seq" />
              </Row>
              <Row>
                <F label="Purity %" v={form.purity_percent} on={(v) => setForm({ ...form, purity_percent: v })} test="f-purity" />
                <F label="Pack size" v={form.pack_size} on={(v) => setForm({ ...form, pack_size: v })} test="f-pack" />
              </Row>
              <Row>
                <F label="Price USD" v={form.price_usd} on={(v) => setForm({ ...form, price_usd: parseFloat(v) || 0 })} test="f-price" />
                <F label="Category" v={form.category} on={(v) => setForm({ ...form, category: v })} test="f-cat" />
              </Row>
              <Row><F label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} test="f-img" /></Row>
              <Row><Ta label="Description (technical only)" v={form.description} on={(v) => setForm({ ...form, description: v })} test="f-desc" /></Row>
              <Row>
                <Sel label="Risk level" v={form.risk_level} options={RISKS} on={(v) => setForm({ ...form, risk_level: v })} test="f-risk" />
                <Sel label="Legal review" v={form.legal_review_status} options={LEGAL} on={(v) => setForm({ ...form, legal_review_status: v })} test="f-legal" />
              </Row>
              <Row>
                <Sel label="US sale" v={form.us_sale_status} options={US} on={(v) => setForm({ ...form, us_sale_status: v })} test="f-us" />
                <Sel label="DEA screen" v={form.dea_screen_status} options={DEA} on={(v) => setForm({ ...form, dea_screen_status: v })} test="f-dea" />
              </Row>
              <Row>
                <Cb label="Allows public listing" v={form.allows_public_listing} on={(v) => setForm({ ...form, allows_public_listing: v })} test="f-public" />
                <Cb label="Allows checkout" v={form.allows_checkout} on={(v) => setForm({ ...form, allows_checkout: v })} test="f-checkout" />
              </Row>
              <Row><Ta label="Notes (admin only)" v={form.notes_for_admin} on={(v) => setForm({ ...form, notes_for_admin: v })} test="f-notes" /></Row>

              {findings.length > 0 && (
                <div className="border border-[#D0021B] bg-[#FCEBED] p-4">
                  <div className="flex items-center gap-2 text-[#A30414] font-semibold"><AlertTriangle className="h-4 w-4" /> Blocked claims detected ({findings.length})</div>
                  <ul className="mt-3 space-y-1 text-xs font-mono">
                    {findings.slice(0, 12).map((f, i) => (
                      <li key={i}><span className="bg-[#D0021B] text-white px-1.5 py-0.5">{f.term}</span> <span className="text-[#5E636E]">…{f.context}…</span></li>
                    ))}
                  </ul>
                  <label className="mt-3 inline-flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="override-toggle" />
                    Compliance admin override (logs an audit entry)
                  </label>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#D1D5DB] sticky bottom-0 bg-white flex justify-end gap-3">
              <button onClick={close} className="btn-outline">Cancel</button>
              <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:bg-[#5E636E]" data-testid="save-product">
                <ShieldCheck className="h-4 w-4" /> {busy ? "Saving…" : (editing === "new" ? "Create product" : "Save changes")}
              </button>
            </div>
          </div>
        </div>
      )}

      {pushModal && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={closePush} data-testid="push-prod-modal">
          <div className="bg-white border border-[#D1D5DB] rounded w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-[#D1D5DB] flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Push to production</div>
                <div className="font-bold tracking-tight mt-0.5">{pushModal.sku}</div>
              </div>
              <button onClick={closePush} aria-label="Close" data-testid="push-prod-close" className="p-1 hover:bg-[#F7F7F8]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="label-eyebrow block mb-1">Production API URL</label>
                <input
                  value={pushForm.prod_api_url}
                  onChange={(e) => setPushForm({ ...pushForm, prod_api_url: e.target.value.trim() })}
                  className="w-full px-3 py-2 border border-[#D1D5DB] font-mono text-sm rounded"
                  data-testid="push-prod-url"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Prod admin email</label>
                <input
                  type="email"
                  value={pushForm.prod_email}
                  onChange={(e) => setPushForm({ ...pushForm, prod_email: e.target.value.trim() })}
                  className="w-full px-3 py-2 border border-[#D1D5DB] text-sm rounded"
                  data-testid="push-prod-email"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Prod admin password</label>
                <input
                  type="password"
                  value={pushForm.prod_password}
                  onChange={(e) => setPushForm({ ...pushForm, prod_password: e.target.value })}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-[#D1D5DB] text-sm rounded"
                  data-testid="push-prod-password"
                />
                <p className="text-[10.5px] font-mono uppercase tracking-wider text-[#5E636E] mt-1">Used once, never stored on the server.</p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={pushForm.override_compliance}
                  onChange={(e) => setPushForm({ ...pushForm, override_compliance: e.target.checked })}
                  className="h-4 w-4 accent-[#003366]"
                  data-testid="push-prod-override"
                />
                <span>Send X-Compliance-Override (recommended for BAC / research-only terms)</span>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-[#D1D5DB] flex justify-end gap-3">
              <button onClick={closePush} className="btn-outline" data-testid="push-prod-cancel">Cancel</button>
              <button
                onClick={runPush}
                disabled={!!pushing}
                className="btn-primary inline-flex items-center gap-2 disabled:bg-[#5E636E]"
                style={{ background: "#0B6623" }}
                data-testid="push-prod-run"
              >
                <UploadCloud className="h-4 w-4" /> {pushing ? "Pushing…" : "Push to production"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
const F = ({ label, v, on, disabled, test }) => (
  <div className="md:col-span-1 col-span-1">
    <label className="label-eyebrow block mb-1">{label}</label>
    <input value={v ?? ""} onChange={(e) => on(e.target.value)} disabled={disabled} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white font-mono text-sm disabled:bg-[#F7F7F8]" data-testid={test} />
  </div>
);
const Ta = ({ label, v, on, test }) => (
  <div className="md:col-span-2 col-span-1">
    <label className="label-eyebrow block mb-1">{label}</label>
    <textarea rows={3} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white text-sm" data-testid={test} />
  </div>
);
const Sel = ({ label, v, on, options, test }) => (
  <div>
    <label className="label-eyebrow block mb-1">{label}</label>
    <select value={v} onChange={(e) => on(e.target.value)} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white font-mono text-sm" data-testid={test}>
      {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ").toUpperCase()}</option>)}
    </select>
  </div>
);
const Cb = ({ label, v, on, test }) => (
  <label className="inline-flex items-center gap-2 text-sm border border-[#D1D5DB] px-3 py-2.5">
    <input type="checkbox" checked={v} onChange={(e) => on(e.target.checked)} data-testid={test} />
    {label}
  </label>
);
